import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
  Fade,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      files.forEach((file, index) => {
        formDataToSend.append(`file_${index}`, file);
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact/send`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: "Message sent successfully! We'll get back to you soon." });
        setSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        setFiles([]);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to send message' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setMessage({ type: 'error', text: 'Failed to send message. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="contact-container ch-page-enter">
      <Box className="contact-inner">
        <Stack spacing={0.75} alignItems="center" textAlign="center" sx={{ mb: 3 }}>
          <Typography variant="h3" component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
            Contact Us
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
            Questions about co-founder discovery, hiring, job posting, investors, or recruitment support? We&apos;d love to hear from you.
          </Typography>
        </Stack>

        {message.text && (
          <Alert severity={message.type === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        <Fade in timeout={220}>
          <Paper className="contact-form ch-card-enter" elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, mb: 2.5 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1.5}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Your name"
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="your@email.com"
                />
                <TextField
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  placeholder="Your message here..."
                  multiline
                  minRows={5}
                />

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
                    Attachments (Optional)
                  </Typography>
                  <Box
                    className="file-input-wrapper"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('files').click()}
                    sx={{
                      border: '1.5px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      py: 2.5,
                      px: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 180ms ease, background-color 180ms ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(7, 132, 90, 0.04)',
                      },
                    }}
                  >
                    <input
                      type="file"
                      id="files"
                      onChange={handleFileChange}
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                      style={{ display: 'none' }}
                    />
                    <CloudUploadOutlinedIcon color="primary" sx={{ mb: 0.5 }} />
                    <Typography variant="body2" color="text.primary" fontWeight={500}>
                      Choose files or drag and drop
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    Supported: Images, PDF, Word, Excel, Text files (Max 10MB each)
                  </Typography>
                </Box>

                {files.length > 0 && (
                  <Stack spacing={0.75}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      Attached files ({files.length})
                    </Typography>
                    {files.map((file, index) => (
                      <Chip
                        key={`${file.name}-${index}`}
                        label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                        onDelete={() => removeFile(index)}
                        deleteIcon={<CloseIcon />}
                        variant="outlined"
                        sx={{ justifyContent: 'space-between', height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                      />
                    ))}
                  </Stack>
                )}

                <Button type="submit" variant="contained" color="primary" disabled={loading || submitted} fullWidth>
                  {submitted ? 'Sent' : loading ? 'Sending...' : 'Send'}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Fade>

        <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="body2">
            Email:{' '}
            <Box component="a" href="mailto:hr@cofounderhubs.com" sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}>
              hr@cofounderhubs.com
            </Box>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default Contact;
